import { cva } from 'class-variance-authority'
import type { ControlSize } from './control'

/**
 * A marker or a button sitting inside a control's padding box.
 *
 * SearchInput, PasswordInput, Datepicker and Combobox each carried their own
 * copy of this geometry, and Combobox's carried a comment saying it mirrored
 * SearchInput's. They agreed, which is the only reason nobody noticed.
 *
 * The control's own padding is not here: that is a class on the input, so it
 * is a variant of `controlVariants`. This module owns only what the adornment
 * itself renders, which is the half that does not belong to the control.
 */
export const adornmentVariants = cva('text-foreground-muted absolute inset-y-0 flex items-center justify-center', {
    variants: {
        side: {
            start: 'left-0',
            end: 'right-0',
            /**
             * The inner of two end markers. Its offset is the width of the
             * outer one, so it is set per size in the compound variants below.
             */
            beforeEnd: ''
        },
        /**
         * The adornment box is exactly the room the control reserves for it.
         *
         * It was horizontal padding, which made the box as wide as whatever it
         * held: a glyph fitted inside the reservation and a three-letter unit
         * did not, so the value ran underneath it. A fixed width cannot
         * disagree with `controlVariants`, and content is centred in it rather
         * than pushed against the edge.
         */
        size: {
            small: 'w-7',
            medium: 'w-9',
            large: 'w-10'
        },
        /**
         * A button rather than a marker.
         *
         * A decorative marker must not swallow a click meant for the input
         * underneath it, which is the bug the donor shipped by making its
         * magnifier a focusable button with no accessible name.
         */
        isInteractive: {
            true: 'hover:text-foreground focus-visible:ring-ring disabled:text-foreground-disabled cursor-pointer rounded-aria-md focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed',
            false: 'pointer-events-none'
        }
    },
    compoundVariants: [
        { side: 'beforeEnd', size: 'small', class: 'right-7' },
        { side: 'beforeEnd', size: 'medium', class: 'right-9' },
        { side: 'beforeEnd', size: 'large', class: 'right-10' }
    ],
    defaultVariants: { side: 'end', size: 'medium', isInteractive: false }
})

/** The glyph inside an adornment, at the size that suits the control. */
export const ADORNMENT_ICON: Record<ControlSize, string> = {
    small: 'size-3',
    medium: 'size-4',
    large: 'size-4'
}
